import { type IUser, queryBuilder } from '@inpart-io/devtools-iap';
import { GET_USER_CURRENT, RECEIVE_DATA_OBJECTS, RETRIEVE_BY_QUERY, SESSION_MODULE, STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import {
    DataType,
    type DefaultCategory,
    DefaultCategoryValues,
    type IPrivateBookmark,
    type IPrivateLeadBoard,
    privateLeadBoardProperties,
    privateLeadBoardsManager
} from '@inpart-io/padme-iap';
import { type ActionContext, type ActionTree } from 'vuex';

import { ALLIANCE_PREFIX } from '@/constants/router-prefixes.constants';
import { addMonths, getLocalDate, getTimeStampLocalDate } from '@/services/date-format';
import { i18nService } from '@/services/i18n-service';

import { PRIVATE_BOOKMARK_MODULE } from '../private-bookmark-module';
import { RECEIVE_DEFAULT_BOARD } from './mutations';

export const RETRIEVE_DEFAULT_BOARDS = 'RETRIEVE_DEFAULT_BOARDS';
export const SHARE_BOARD = 'SHARE_BOARD';

const defaultBoardsValues: {
    [key in DefaultCategory]: Record<string, (context?: ActionContext<IPrivateLeadBoard, unknown>) => Partial<IPrivateLeadBoard>>;
} = {
    [DefaultCategoryValues.ALL]: {
        [DataType.Lead]: () => {
            return {
                name: i18nService.t('BOARD.ALL', { objects: i18nService.t('LEAD.LOW.PLUR') }),
                criteria: 'statusTab='
            };
        },
        [DataType.Meeting]: () => {
            return {
                name: i18nService.t('BOARD.ALL', { objects: i18nService.t('MEETING.LOW.PLUR') }),
                criteria: ''
            };
        },
        [DataType.Agreement]: () => {
            return {
                name: i18nService.t('BOARD.ALL', { objects: i18nService.t('AGREEMENT.LOW.PLUR') }),
                criteria: ''
            };
        },
        [DataType.Alliance]: () => {
            return {
                name: i18nService.t('BOARD.ALL', { objects: i18nService.t('ALLIANCE.LOW.PLUR') }),
                criteria: ''
            };
        },
        [DataType.Obligation]: () => {
            return {
                name: i18nService.t('BOARD.ALL', { objects: i18nService.t('OBLIGATION.LOW.PLUR') }),
                criteria: ''
            };
        },
        [DataType.PrivateCompany]: () => {
            return {
                name: `All ${i18nService.t('COMPANY.LOW.PLUR')}`
            };
        }
    },
    [DefaultCategoryValues.MY]: {
        [DataType.Lead]: (context: ActionContext<IPrivateLeadBoard, unknown>) => {
            const currentUser: IUser = context.rootGetters[`${SESSION_MODULE}/${GET_USER_CURRENT}`];

            return {
                name: i18nService.t('BOARD.MY', { objects: i18nService.t('LEAD.LOW.PLUR') }),
                criteria: `owners=${currentUser.dobjId}`
            };
        },
        [DataType.Meeting]: (context: ActionContext<IPrivateLeadBoard, unknown>) => {
            const currentUser: IUser = context.rootGetters[`${SESSION_MODULE}/${GET_USER_CURRENT}`];

            return {
                name: i18nService.t('BOARD.MY', { objects: i18nService.t('MEETING.LOW.PLUR') }),
                criteria: `userAttendees=${currentUser.dobjId}`
            };
        },
        [DataType.Agreement]: (context: ActionContext<IPrivateLeadBoard, unknown>) => {
            const currentUser: IUser = context.rootGetters[`${SESSION_MODULE}/${GET_USER_CURRENT}`];

            return {
                name: i18nService.t('BOARD.MY', { objects: i18nService.t('AGREEMENT.LOW.PLUR') }),
                criteria: `owner=${currentUser.dobjId}`
            };
        },
        [DataType.Alliance]: (context: ActionContext<IPrivateLeadBoard, unknown>) => {
            const currentUser: IUser = context.rootGetters[`${SESSION_MODULE}/${GET_USER_CURRENT}`];

            return {
                name: i18nService.t('BOARD.MY', { objects: i18nService.t('ALLIANCE.LOW.PLUR') }),
                criteria: `${ALLIANCE_PREFIX}owner=${currentUser.dobjId}`
            };
        },
        [DataType.Obligation]: (context: ActionContext<IPrivateLeadBoard, unknown>) => {
            const currentUser: IUser = context.rootGetters[`${SESSION_MODULE}/${GET_USER_CURRENT}`];

            return {
                name: i18nService.t('BOARD.MY', { objects: i18nService.t('OBLIGATION.LOW.PLUR') }),
                criteria: `owner=${currentUser.dobjId}`
            };
        }
    },
    [DefaultCategoryValues.UPCOMING]: {
        [DataType.Meeting]: () => {
            const now = getTimeStampLocalDate(getLocalDate());

            return {
                name: i18nService.t('BOARD.UPCOMING', { objects: i18nService.t('MEETING.LOW.PLUR') }),
                criteria: `dateBegin=${now}`
            };
        }
    },
    [DefaultCategoryValues.EXPIRE_SOON]: {
        [DataType.Agreement]: () => {
            const now = getTimeStampLocalDate(getLocalDate());
            const in3months = getTimeStampLocalDate(addMonths(getLocalDate(), 3));

            return {
                name: i18nService.t('BOARD.EXPIRE_SOON'),
                criteria: `expirationDateBegin=${now}&expirationDateEnd=${in3months}`
            };
        },
        [DataType.Alliance]: () => {
            const now = getTimeStampLocalDate(getLocalDate());
            const in3months = getTimeStampLocalDate(addMonths(getLocalDate(), 3));

            return {
                name: i18nService.t('BOARD.EXPIRE_SOON'),
                criteria: `expirationDateBegin=${now}&expirationDateEnd=${in3months}`
            };
        }
    },
    [DefaultCategoryValues.UNMET]: {
        [DataType.PrivateCompany]: () => {
            return {
                name: `Unmet ${i18nService.t('COMPANY.LOW.PLUR')}`,
                criteria: `lastMeetingDateIsNull=true`,
                visibleColumns: ['name', 'address.country.label', '#leads', 'agreementActiveTypes']
            };
        }
    }
};

export const actions: ActionTree<IPrivateLeadBoard, unknown> = {
    [RETRIEVE_DEFAULT_BOARDS](context, { isManager }: { isManager: boolean }) {
        const comparators = [queryBuilder.cmp.isNotNull('defaultViewCategory')];

        if (!isManager) {
            comparators.push(queryBuilder.cmp.isNotIn('defaultViewCategory', [DefaultCategoryValues.MY]));
        }

        void context.dispatch(RETRIEVE_BY_QUERY, {
            query: queryBuilder.build(queryBuilder.op.and(...comparators), undefined, undefined, [{ attribute: 'name', direction: 'ASC' }]),
            properties: privateLeadBoardProperties.DEFAULT
        });
    },
    async [SHARE_BOARD](context, { boardId, properties }: { boardId: number; properties?: string }): Promise<void> {
        const board = await privateLeadBoardsManager.shareBoard(boardId, { properties });
        context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: [board] });
    },
    [STORE_DATA_OBJECTS](context, { dataObjectList }: { dataObjectList: IPrivateLeadBoard[] }) {
        const boards: IPrivateLeadBoard[] = [];
        const bookmarks: IPrivateBookmark[] = [];

        dataObjectList.forEach((board) => {
            let _board = Object.assign({}, board);

            if (board.defaultViewCategory !== null) {
                context.commit(RECEIVE_DEFAULT_BOARD, { board });
                const partialBoard =
                    defaultBoardsValues[board.defaultViewCategory]?.[board.targetType] !== undefined
                        ? defaultBoardsValues[board.defaultViewCategory][board.targetType](context)
                        : undefined;
                _board = Object.assign(board, partialBoard);
            }

            // Extract Bookmark object if necessary
            if (board?.['@bookmark']?.length > 0) {
                const bookmark = board['@bookmark'][0];
                bookmarks.push(bookmark);
                _board = Object.assign({}, _board, { bookmark: bookmark.dobjId, '@bookmark': undefined });
            }

            boards.push(_board);
        });
        if (boards.length > 0) {
            context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList: boards });
        }
        if (bookmarks.length > 0) {
            void context.dispatch(`${PRIVATE_BOOKMARK_MODULE}/${STORE_DATA_OBJECTS}`, { dataObjectList: bookmarks }, { root: true });
        }
    }
};
